import {
    get as _get,
} from '@lodash';
import { Dispatch, Commit } from 'vuex';
import StoreUtil from '@app/store/StoreUtil';
import Match from '@core/models/offer/Match';
import MediaService from '@core/services/media/MediaService';
import { IState } from '@app/store';
import { VideoType } from '@core/services/media/enums';
import { ExpandedEventInUrl } from '@app/enums';
import search, { ISearchState } from './search/searchIndex';
import sports, { ISportsState } from './sports/sportsIndex';
import filter, { IFilterState } from './filter/filterIndex';
import betSlip from './betSlip/betSlipIndex';
import IBetSlipState from '@store/modules/ui/shared/betSlip/types';
import teams, { ITeamsState } from './teams/teamsIndex';
import { StreamState } from './enums';
import { removeEventQueryParams } from '@app/utils';
import { Getters } from './types';
import OfferService from '@core/services/offer/OfferService';

const mediaService = new MediaService();

const SET_EVENT_SELECTION = 'SET_EVENT_SELECTION';
const SET_DOCKED_STREAM = 'SET_DOCKED_STREAM';
const SET_FLOATING_STREAM = 'SET_FLOATING_STREAM';
const CLEAR_DOCKED_STREAM = 'CLEAR_DOCKED_STREAM';
const CLOSE_FLOATING_STREAM = 'CLOSE_FLOATING_STREAM';
const CHANGE_STREAM_STATE = 'CHANGE_STREAM_STATE';
const SET_SELECTED_EVENT_HEIGHT = 'SET_SELECTED_EVENT_HEIGHT';
const TOGGLE_DOCKED_VIDEO_SOUND = 'TOGGLE_DOCKED_VIDEO_SOUND';
const TOGGLE_FLOATING_VIDEO_SOUND = 'TOGGLE_FLOATING_VIDEO_SOUND';
const SET_HAS_MATCH_VISUALISATION = 'SET_HAS_MATCH_VISUALISATION';

interface VideoPlayerData {
    state: StreamState;
    matchId: number;
    isSoundOn: boolean;
}
type VideoPlayerState = null | VideoPlayerData | StreamDataFull;

interface StreamDataFull extends VideoPlayerData {
    streamUrl: string;
    videoType: VideoType;
}

interface ISportOfferStateBase {
    dockedVideoStream: VideoPlayerState;
    floatingVideoStream: VideoPlayerState;
    selectedEventHeight: number | null;
    matchVisualisations: Record<Match['id'], boolean>;
}

export interface ISportOfferState extends ISportOfferStateBase {
    sports: ISportsState;
    filter: IFilterState;
    search: ISearchState;
    teams: ITeamsState;
    betSlip: IBetSlipState;
}

const getters: Getters =  {
    expandedEvent: (state: ISportOfferState, getters: Getters) => {
        return getters.expandedEventRow.id;
    },
    expandedEventRow: (state: ISportOfferState, getters: any, rootState: IState, rootGetters: any) => {
        let eventId = rootGetters['navigation/route'].query[ExpandedEventInUrl.event];
        eventId = eventId ? parseInt(eventId, 10) : null;

        if (!rootGetters['data/sportOffer/eventsMap'][eventId]
            || rootGetters['data/sportOffer/eventsMap'][eventId].isFinished()) {
            return {
                typeId: null,
                id: null,
            };
        }

        const typeId = rootGetters['navigation/route'].query[ExpandedEventInUrl.type];

        return {
            typeId,
            id: eventId
        };
    },
    expandedEventId: (state: ISportOfferState, getters: any) => {
        return getters.expandedEventRow.id;
    },
    dockedVideoStream: StoreUtil.createSimpleGetter('dockedVideoStream'),
    floatingVideoStream: StoreUtil.createSimpleGetter('floatingVideoStream'),
    selectedEventHeight: StoreUtil.createSimpleGetter('selectedEventHeight'),
    shouldDisplayDockedStream: (state: ISportOfferState) => {
        return state.dockedVideoStream
        && _get(state, 'dockedVideoStream.matchId') === _get(state, 'floatingVideoStream.matchId');
    },
    matchHasVisualisation(state: ISportOfferStateBase) {
        return (matchId: Match['id']) => state.matchVisualisations[matchId];
    },
    // visualisationUrl() {
    //     return (betRadarId: Exclude<Match['betRadarId'], undefined>) =>
    //         OfferService.getInstance().getVisualisationUrl(betRadarId);
    // }
};

export default {
    getters,
    state: {
        selectedEventHeight: null,
        dockedVideoStream: null,
        floatingVideoStream: null,
        matchVisualisations: {},
    } as ISportOfferStateBase,

    mutations: {
        [SET_DOCKED_STREAM]: (state: ISportOfferState, streamData: VideoPlayerData | StreamDataFull) => {
            state.dockedVideoStream = streamData;
        },
        [SET_FLOATING_STREAM]: (state: ISportOfferState, streamData: VideoPlayerData | StreamDataFull) => {
            state.floatingVideoStream = streamData;
        },
        [CLEAR_DOCKED_STREAM]: (state: ISportOfferState) => {
            state.dockedVideoStream = null;
        },
        [CLOSE_FLOATING_STREAM]: (state: ISportOfferState) => {
            state.floatingVideoStream = null;
        },
        [CHANGE_STREAM_STATE]: (state: ISportOfferState, streamState: StreamState) => {
            if (state.dockedVideoStream) {
                state.dockedVideoStream.state = streamState;
            }
        },
        [SET_SELECTED_EVENT_HEIGHT]: StoreUtil.createSimpleMutator('selectedEventHeight'),
        [TOGGLE_DOCKED_VIDEO_SOUND]: StoreUtil.createToggleMutator('dockedVideoStream.isSoundOn'),
        [TOGGLE_FLOATING_VIDEO_SOUND]: StoreUtil.createToggleMutator('floatingVideoStream.isSoundOn'),
        [SET_HAS_MATCH_VISUALISATION]: StoreUtil.createSetMutator('matchVisualisations'),
    },

    actions: {
        setSelectedEventHeight: StoreUtil.createSimpleMutatorAction(SET_SELECTED_EVENT_HEIGHT),
        toggleExpandedEventSubscription: (
            { dispatch, rootGetters }: { dispatch: Dispatch, rootGetters: any },
            { eventId, oldEventId }: { eventId: number | null, oldEventId: number | null }
        ) => {
            if (oldEventId && rootGetters['data/sportOffer/subscriptions'][oldEventId]) {
                dispatch('data/sportOffer/removeSubscription', oldEventId, { root: true });
            }

            if (eventId) {
                dispatch('data/sportOffer/getEvent', eventId, { root: true });
            }
        },
        toggleExpandedEvent: (
            { getters, rootGetters, dispatch }:
                { dispatch: Dispatch, getters: any, rootGetters: any },
            { eventId, typeId }: { eventId: number, typeId: number }
        ) => {
            let query = rootGetters['navigation/route'].query;

            if (getters.expandedEvent === eventId) {
                query = removeEventQueryParams(query);
            } else {
                query = {
                    ...query,
                    [ExpandedEventInUrl.event]: eventId.toString(),
                    [ExpandedEventInUrl.type]: typeId.toString()
                };
            }

            dispatch(
                'navigation/push',
                {
                    query,
                    path: rootGetters['navigation/route'].path,
                },
                { root: true }
            );
        },
        cancelEventSubscriptions: ({ dispatch, commit, getters }:
            { dispatch: Dispatch, commit: Commit, getters: any }) => {
            if (getters.expandedEventRow.id) {
                dispatch('data/sportOffer/removeSubscription', getters.expandedEventRow.id, { root: true });
                commit(SET_EVENT_SELECTION, null);
            }
        },
        unpinVideo:  ({ state, commit }: { state: ISportOfferState, commit: Commit }) => {
            if (state.dockedVideoStream) {
                const dockedStreamState = { ... state.dockedVideoStream };
                commit(SET_FLOATING_STREAM, dockedStreamState);
            }
        },
        pinVideo:  ({ commit, state }: { commit: Commit, state: ISportOfferState }) => {
            if (!state.dockedVideoStream) {
                commit(SET_DOCKED_STREAM, state.floatingVideoStream);
            } else if (state.floatingVideoStream!.isSoundOn !== state.dockedVideoStream!.isSoundOn) {
                commit(TOGGLE_DOCKED_VIDEO_SOUND);
            }
            commit(CLOSE_FLOATING_STREAM);
        },
        closeDockedStream ({ commit }: { commit: Commit }) {
            commit(CHANGE_STREAM_STATE, StreamState.Closed);
        },
        closeFloatingStream: StoreUtil.createSimpleMutatorAction(CLOSE_FLOATING_STREAM),
        clearDockedStream: StoreUtil.createSimpleMutatorAction(CLEAR_DOCKED_STREAM),
        playStream({ commit, state }: { commit: Commit, state: ISportOfferStateBase }) {
            const streamData = state.dockedVideoStream;
            commit(SET_DOCKED_STREAM, {
                ...streamData,
                state: StreamState.Playing,
            });
        },
        prepareStream: StoreUtil.asyncErrorGuard(async (
            { commit, state }: { commit: Commit, state: ISportOfferStateBase },
            match: Match) => {
            const videoPlayerData: VideoPlayerData = {
                matchId: match.id,
                state: StreamState.Loading,
                isSoundOn: state.dockedVideoStream ? state.dockedVideoStream.isSoundOn : true,
            };
            commit(SET_DOCKED_STREAM, videoPlayerData);
            try {
                const {
                    streamUrl,
                    videoType,
                } = await mediaService.getMatchVideoStream(match);
                const streamData = {
                    ...videoPlayerData,
                    streamUrl,
                    videoType,
                    state: StreamState.NotStarted,
                };
                commit(SET_DOCKED_STREAM, streamData);
            } catch (e) {
                commit(CHANGE_STREAM_STATE, StreamState.NotAvailable);
            }
        }),
        toggleDockedVideoSound: StoreUtil.createSimpleMutatorAction(TOGGLE_DOCKED_VIDEO_SOUND),
        toggleFloatingVideoSound: StoreUtil.createSimpleMutatorAction(TOGGLE_FLOATING_VIDEO_SOUND),
        // async checkMatchHasVisualisation(
        //     { commit }: { commit: Commit },
        //     { id, betRadarId, sportId }: { id: number, betRadarId?: string, sportId: number }) {
        //     const hasVisualisation = await OfferService.getInstance().checkMatchHasVisualisation(betRadarId, sportId);
        //     commit(SET_HAS_MATCH_VISUALISATION, { key: id, value: hasVisualisation });
        // }
    },

    modules: {
        sports,
        filter,
        search,
        teams,
        betSlip,
    },

    namespaced: true,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/ui/sportOffer/sportOfferIndex.ts